import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { SyncModelKind } from "@/store/sync/types";
import { action, computed, makeObservable, observable } from "mobx";
import { api } from "@/modules/api";
import { logger } from "@/modules/logger";
import { NoteContentVersion } from "@/store/note/types";
import {
  NoteVersionSnapshotSummary,
  NoteContentDocumentSnapshot,
} from "@/store/note-version-history/types";
import { NoteContentDocumentModelData } from "@/store/note/types";

// Page-scoped Note Version History store for NotesViewPageStore
export class NoteVersionHistoryPageStore extends AppSubStore {
  snapshots: NoteVersionSnapshotSummary[] = [];
  selectedSnapshot: NoteVersionSnapshotSummary | null = null;
  selectedSnapshotContent: NoteContentDocumentSnapshot | null = null;
  previousSnapshotContent: NoteContentDocumentSnapshot | null = null;
  isLoadingSnapshots: boolean = false;
  isLoadingContent: boolean = false;
  error: string = "";
  currentNoteId: string | null = null;

  shouldShowDifferences: boolean = false;
  versionForDifferences: NoteContentVersion | null = null;
  baseVersionForDifferences: NoteContentVersion | null = null;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<
      this,
      | "setSnapshots"
      | "setSelectedSnapshot"
      | "setSelectedSnapshotContent"
      | "setPreviousSnapshotContent"
      | "setIsLoadingSnapshots"
      | "setIsLoadingContent"
      | "setError"
      | "loadSnapshotContent"
      | "showSnapshotDifferences"
      | "convertSnapshotToNoteContentVersion"
      | "setShouldShowDifferences"
      | "setVersionForDifferences"
      | "setBaseVersionForDifferences"
    >(this, {
      selectNoteSnapshotByVersion: true,
      loadSnapshotContent: true,
      showSnapshotDifferences: true,
      convertSnapshotToNoteContentVersion: true,
      snapshots: observable,
      selectedSnapshot: observable,
      selectedSnapshotContent: observable,
      previousSnapshotContent: observable,
      isLoadingSnapshots: observable,
      isLoadingContent: observable,
      error: observable,
      currentNoteId: observable,
      shouldShowDifferences: observable,
      versionForDifferences: observable,
      baseVersionForDifferences: observable,

      openNoteVersionHistory: action,
      closeNoteVersionHistory: action,
      selectNoteSnapshot: action,
      restoreSelectedNoteSnapshot: action,
      setSnapshots: action,
      setSelectedSnapshot: action,
      setSelectedSnapshotContent: action,
      setPreviousSnapshotContent: action,
      setIsLoadingSnapshots: action,
      setIsLoadingContent: action,
      setError: action,
      setShouldShowDifferences: action,
      setVersionForDifferences: action,
      setBaseVersionForDifferences: action,

      isLatestSelected: computed,
      canRestore: computed,
      latestVersionId: computed,
      isLoading: computed,
    });
  }

  public async openNoteVersionHistory(noteId: string, version?: number) {
    this.currentNoteId = noteId;
    this.setIsLoadingSnapshots(true);
    this.setSnapshots([]);
    this.setError("");

    // Reset transient selection/diff state to avoid leaking from previous note
    this.selectedSnapshot = null;
    this.selectedSnapshotContent = null;
    this.previousSnapshotContent = null;
    this.setShouldShowDifferences(false);
    this.setVersionForDifferences(null);
    this.setBaseVersionForDifferences(null);

    try {
      const response = await api.post("/v2/note-content-document-snapshots/search", {
        body: {
          note_id: noteId,
          offset: 0,
          order_by_asc: false,
        },
      });

      if (!response.data?.results) {
        return;
      }

      this.setSnapshots(response.data.results);

      if (response.data.results.length > 0) {
        if (version !== undefined) {
          await this.selectNoteSnapshotByVersion(version);
        } else {
          await this.selectNoteSnapshot(0);
        }
      }
    } catch (error) {
      logger.error({
        message: "[NoteVersionHistoryPageStore] Failed to load snapshots",
        info: { error: String(error) },
      });
      this.setError("Failed to load version history");
    } finally {
      this.setIsLoadingSnapshots(false);
    }
  }

  public async selectNoteSnapshotByVersion(version: number) {
    const snapshotIndex = this.snapshots.findIndex(
      snapshot => snapshot.note_content_document_version === version
    );
    if (snapshotIndex === -1) {
      logger.error({
        message: "[NoteVersionHistoryPageStore] Snapshot not found for version",
        info: {
          version,
          availableVersions: this.snapshots.map(s => s.note_content_document_version),
        },
      });
      return;
    }

    await this.selectNoteSnapshot(snapshotIndex);
  }

  public async selectNoteSnapshot(snapshotIndex: number) {
    const snapshot = this.snapshots[snapshotIndex];
    if (!snapshot) {
      logger.error({
        message: "[NoteVersionHistoryPageStore] Invalid snapshot index",
        info: { snapshotIndex, totalSnapshots: this.snapshots.length },
      });
      return;
    }

    this.setIsLoadingContent(true);
    this.setSelectedSnapshot(snapshot);
    this.setError("");

    try {
      const loadPromises: Promise<NoteContentDocumentSnapshot | null>[] = [
        this.loadSnapshotContent(snapshot.id),
      ];

      let previousSnapshot: NoteVersionSnapshotSummary | null = null;
      if (snapshotIndex < this.snapshots.length - 1) {
        previousSnapshot = this.snapshots[snapshotIndex + 1];
        if (previousSnapshot) {
          loadPromises.push(this.loadSnapshotContent(previousSnapshot.id));
        }
      }

      const [snapshotContent, previousSnapshotContent] = await Promise.all(loadPromises);

      if (!snapshotContent) {
        return;
      }

      this.setSelectedSnapshotContent(snapshotContent);
      this.setPreviousSnapshotContent(previousSnapshotContent || null);
      this.showSnapshotDifferences(snapshotContent);
    } catch (error) {
      logger.error({
        message: "[NoteVersionHistoryPageStore] Failed to load snapshot content",
        info: { error: String(error) },
      });
      this.setError("Failed to load snapshot content");
    } finally {
      this.setIsLoadingContent(false);
    }
  }

  public closeNoteVersionHistory() {
    this.snapshots = [];
    this.selectedSnapshot = null;
    this.selectedSnapshotContent = null;
    this.previousSnapshotContent = null;
    this.error = "";
    this.currentNoteId = null;
    this.isLoadingSnapshots = false;
    this.isLoadingContent = false;

    this.setShouldShowDifferences(false);
    this.setVersionForDifferences(null);
    this.setBaseVersionForDifferences(null);
  }

  public async restoreSelectedNoteSnapshot() {
    if (!this.selectedSnapshotContent || !this.currentNoteId) {
      logger.warn({
        message: "[NoteVersionHistoryPageStore] Cannot restore - missing content or noteId",
      });
      return;
    }

    const note = this.store.notes.getNoteObservableById({ noteId: this.currentNoteId });
    if (!note) {
      logger.error({
        message: "[NoteVersionHistoryPageStore] Cannot restore - note not found",
        info: { noteId: this.currentNoteId },
      });
      return;
    }

    try {
      const versionToRestore = this.convertSnapshotToNoteContentVersion(
        this.selectedSnapshotContent
      );

      note.acceptNoteContentChanges(versionToRestore, undefined, {
        overwrite: true,
        patchId: `snapshot-${this.selectedSnapshotContent.id}`,
      });

      this.closeNoteVersionHistory();
    } catch (error) {
      logger.error({
        message: "[NoteVersionHistoryPageStore] Failed to restore snapshot",
        info: { error: String(error) },
      });
      this.setError("Failed to restore version");
    }
  }

  public get isLatestSelected(): boolean {
    return !!(this.selectedSnapshot && this.snapshots[0]?.id === this.selectedSnapshot.id);
  }

  public get latestVersionId(): string | undefined {
    return this.snapshots[0]?.id;
  }

  public get canRestore(): boolean {
    return (
      !this.isLatestSelected &&
      !!this.selectedSnapshotContent &&
      !this.isLoadingContent &&
      !this.isLoadingSnapshots
    );
  }

  public get isLoading(): boolean {
    return this.isLoadingSnapshots || this.isLoadingContent;
  }

  private async loadSnapshotContent(
    snapshotId: string
  ): Promise<NoteContentDocumentSnapshot | null> {
    try {
      const response = await api.get(
        `/v2/note-content-document-snapshots/{note_content_document_snapshot_id}`,
        {
          params: {
            path: {
              note_content_document_snapshot_id: snapshotId,
            },
          },
        }
      );

      return response.data || null;
    } catch (error) {
      logger.error({
        message: "[NoteVersionHistoryPageStore] Failed to load snapshot content",
        info: { snapshotId, error: String(error) },
      });
      throw error;
    }
  }

  private showSnapshotDifferences(snapshot: NoteContentDocumentSnapshot) {
    const versionData: NoteContentVersion = this.convertSnapshotToNoteContentVersion(snapshot);
    let baseVersionData: NoteContentVersion | undefined = undefined;

    if (this.previousSnapshotContent) {
      baseVersionData = this.convertSnapshotToNoteContentVersion(this.previousSnapshotContent);
    }

    this.setVersionForDifferences(versionData);
    this.setBaseVersionForDifferences(baseVersionData || null);
    this.setShouldShowDifferences(true);
  }

  private convertSnapshotToNoteContentVersion(
    snapshot: NoteContentDocumentSnapshot
  ): NoteContentVersion {
    return {
      model_id: snapshot.note_content_document_id,
      model_kind: SyncModelKind.NoteContentDocument,
      model_version: snapshot.note_content_document_version,
      model_data: {
        encoded_content: snapshot.encoded_content,
        note_id: this.currentNoteId!,
      } as NoteContentDocumentModelData,
      model_scopes: [],
    };
  }

  private setSnapshots(snapshots: NoteVersionSnapshotSummary[]) {
    this.snapshots = snapshots;
  }

  private setSelectedSnapshot(snapshot: NoteVersionSnapshotSummary) {
    this.selectedSnapshot = snapshot;
  }

  private setSelectedSnapshotContent(content: NoteContentDocumentSnapshot | null) {
    this.selectedSnapshotContent = content;
  }

  private setPreviousSnapshotContent(content: NoteContentDocumentSnapshot | null) {
    this.previousSnapshotContent = content;
  }

  private setIsLoadingSnapshots(loading: boolean) {
    this.isLoadingSnapshots = loading;
  }

  private setIsLoadingContent(loading: boolean) {
    this.isLoadingContent = loading;
  }

  private setError(error: string) {
    this.error = error;
  }

  private setShouldShowDifferences(shouldShow: boolean) {
    this.shouldShowDifferences = shouldShow;
  }

  private setVersionForDifferences(version: NoteContentVersion | null) {
    this.versionForDifferences = version;
  }

  private setBaseVersionForDifferences(baseVersion: NoteContentVersion | null) {
    this.baseVersionForDifferences = baseVersion;
  }
}
